import { useState } from 'react'
import { Modal, Stack, Select, Textarea, Button, Alert } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { organizationsApi, type OrgUser } from '@/api/organizations'
import { formatApiError } from '@/api/errors'

// Mensagens PT-BR dos guards de transferência (spec 2026-07-24 §3.1).
const TRANSFER_ERROR_MESSAGES: Record<string, string> = {
  gestor_cannot_manage_gestor: 'Apenas administradores podem gerenciar gestores/responsável.',
  pending_request_exists: 'Este colaborador já possui uma solicitação pendente.',
}

function formatTransferError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && TRANSFER_ERROR_MESSAGES[detail]) return TRANSFER_ERROR_MESSAGES[detail]
  return formatApiError(err, fallback)
}

/**
 * Modal de transferência de um colaborador (member) para outra organização.
 * O colaborador perde acesso na hora e o destino precisa aprovar (spec §3.1).
 */
export function TransferUserModal({
  fromOrgId, user, opened, onClose,
}: {
  fromOrgId: number
  user: OrgUser | null
  opened: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [toOrg, setToOrg] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Lista id+nome de todas as orgs — destino da transferência.
  const { data: orgs = [] } = useQuery({
    queryKey: ['organizations-lite'],
    queryFn: organizationsApi.listLite,
    enabled: opened,
  })

  const options = orgs
    .filter(o => o.id !== fromOrgId)
    .map(o => ({ value: String(o.id), label: o.name }))

  const reset = () => { setToOrg(null); setNote(''); setSubmitting(false) }
  const handleClose = () => { reset(); onClose() }

  const handleSubmit = async () => {
    if (!user || !toOrg) return
    setSubmitting(true)
    try {
      await organizationsApi.transferOrgUser(fromOrgId, user.user_id, {
        to_org_id: Number(toOrg),
        note: note.trim() || undefined,
      })
      qc.invalidateQueries({ queryKey: ['org-users', fromOrgId] })
      qc.invalidateQueries({ queryKey: ['membership-requests', fromOrgId] })
      qc.invalidateQueries({ queryKey: ['organizations'] })
      notifications.show({
        title: 'Transferência solicitada',
        message: `${user.full_name || user.email} foi indicado(a) para a organização de destino`,
        color: 'green',
      })
      handleClose()
    } catch (err) {
      notifications.show({ title: 'Erro', message: formatTransferError(err, 'Falha ao solicitar transferência'), color: 'red' })
      setSubmitting(false)
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={user ? `Transferir ${user.full_name || user.email}` : 'Transferir colaborador'}>
      <Stack gap="sm">
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          O colaborador perde o acesso até a aprovação da organização de destino.
        </Alert>
        <Select
          label="Organização de destino"
          placeholder="Selecione a organização"
          data={options}
          value={toOrg}
          onChange={setToOrg}
          searchable
          withAsterisk
        />
        <Textarea
          label="Observação (opcional)"
          placeholder="Justificativa da transferência"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          maxLength={500}
          autosize
          minRows={2}
        />
        <Button onClick={handleSubmit} loading={submitting} disabled={!toOrg}>
          Solicitar transferência
        </Button>
      </Stack>
    </Modal>
  )
}
